import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { ProjectService } from '../../../../services/project.service';
import { ProjectSprintData } from '../../services/project.sprint.data.service';
import { GfTranslateService } from '../../../../services/core.translate.service';

@Component({
    selector: 'project-sprint-burndown',
    template: require('./project.sprint.burndown.component.html'),
})
export class ProjectSprintBurndownComponent implements OnInit, OnChanges {
    @Input() project: any;
    @Input() sprintId?: number | string;
    @Input() sprint?: any;
    @Input() burndown?: any;

    loading = false;
    statusMessage = '';
    chartOptions: any = null;
    chartData: any[] = [];
    sprintName = '';
    projectedEnd = '';

    private requestToken = 0;

    constructor(
        private projectService: ProjectService,
        private projectSprintData: ProjectSprintData,
        private translateService: GfTranslateService
    ) {}

    ngOnInit(): void {
        this.loadBurndown();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const projectChanged = changes.project && !changes.project.firstChange;
        const sprintIdChanged = changes.sprintId && !changes.sprintId.firstChange;
        const sprintChanged = changes.sprint && !changes.sprint.firstChange;
        const burndownChanged = changes.burndown && !changes.burndown.firstChange;

        if (projectChanged || sprintIdChanged || sprintChanged || burndownChanged) {
            this.loadBurndown();
        }
    }

    private loadBurndown(): void {
        this.clearChart();

        if (this.applyProvidedBurndown()) {
            this.loading = false;
            return;
        }

        const unixName = this.project?.unixName ?? this.project?.unixname;
        if (!this.project || !unixName) {
            this.setStatusMessage(this.translateService.translate('Project data is not available for the burndown widget.'));
            return;
        }

        const activeRequest = ++this.requestToken;
        this.loading = true;
        this.statusMessage = '';

        const resolveSprintId = this.resolveSprintId(unixName);

        resolveSprintId
            .then((resolvedSprintId) => {
                if (activeRequest !== this.requestToken) {
                    return null;
                }

                if (!resolvedSprintId) {
                    this.handleNoSprint();
                    return null;
                }

                return this.projectService.sprintDetails(resolvedSprintId, { rel: 'burndown' })
                    .then((details) => {
                        if (activeRequest !== this.requestToken) {
                            return;
                        }
                        this.applySprintDetails(details);
                    })
                    .catch(() => {
                        if (activeRequest !== this.requestToken) {
                            return;
                        }
                        this.handleLoadFailure();
                    });
            })
            .catch(() => {
                if (activeRequest !== this.requestToken) {
                    return;
                }
                this.handleLoadFailure();
            })
            .finally(() => {
                if (activeRequest === this.requestToken) {
                    this.loading = false;
                }
            });
    }

    private resolveSprintId(unixName: string): Promise<number | null> {
        const parsedInputId = this.parseSprintId(this.sprintId);
        if (typeof parsedInputId === 'number') {
            return Promise.resolve(parsedInputId);
        }

        const sprintInputId = this.parseSprintId(this.sprint?.id);
        if (typeof sprintInputId === 'number') {
            return Promise.resolve(sprintInputId);
        }

        return this.projectService.sprints(unixName, {
            page_size: 1,
            page_num: 1,
            sort_field: 'sprint_end',
            sort_dir: 'desc',
            sprint_status: 1,
        }).then((activeResponse: any) => {
            const activeSprint = activeResponse?.items?.[0];
            if (activeSprint?.id) {
                return activeSprint.id;
            }

            return this.projectService.sprints(unixName, {
                page_size: 1,
                page_num: 1,
                sort_field: 'sprint_end',
                sort_dir: 'desc',
            }).then((latestResponse: any) => {
                const latestSprint = latestResponse?.items?.[0];
                return latestSprint?.id ?? null;
            }).catch(() => null);
        }).catch(() => null);
    }

    private parseSprintId(value: any): number | undefined {
        if (typeof value === 'number' && !isNaN(value)) {
            return value;
        }

        if (typeof value === 'string') {
            const parsed = parseInt(value, 10);
            if (!isNaN(parsed)) {
                return parsed;
            }
        }

        return undefined;
    }

    private applyProvidedBurndown(): boolean {
        const burndownSource = this.burndown ?? this.sprint?.rel?.burndown;
        if (!burndownSource) {
            return false;
        }

        const details = this.sprint ?? { rel: { burndown: burndownSource } };
        this.applySprintDetails(details);
        return true;
    }

    private applySprintDetails(sprintDetails: any): void {
        this.sprintName = this.projectSprintData.getSprintTitle(sprintDetails);
        this.projectedEnd = sprintDetails?.rel?.burndown?.projected_end ?? '';

        const bins = sprintDetails?.rel?.burndown?.bins ?? null;
        if (!bins || Object.keys(bins).length === 0) {
            this.setStatusMessage(this.translateService.translate('No burndown data available for the selected sprint.'));
            return;
        }

        this.chartData = this.projectSprintData.getBurndownChartData(bins);
        this.chartOptions = this.projectSprintData.getBurndownChartOptions(bins);
        this.statusMessage = '';
    }

    private handleNoSprint(): void {
        this.setStatusMessage(this.translateService.translate('No sprint data available for this project.'));
    }

    private handleLoadFailure(): void {
        this.setStatusMessage(this.translateService.translate('There was a problem loading the burndown data.'));
    }

    private setStatusMessage(message: string): void {
        this.statusMessage = message;
    }

    private clearChart(): void {
        this.chartData = [];
        this.chartOptions = null;
        this.sprintName = '';
        this.projectedEnd = '';
    }
}
